import { useState } from 'react';

import { useUpdateReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import {
  type LensDatasetDryRunResponse,
  useDryRunReportingLeLensDataset,
} from '~/hooks/apis/reporting/useLeLensDatasetChecks';

import type { Lens, LensDataset } from '~/types/leLens';

/**
 * F10 (01_USER_FLOWS.md), the per-dataset fallback: "Declare now" on a legacy dataset that has
 * no `columns` yet (02_RIGHT_PANEL_SPEC.md's "Dataset has no dry run yet" state, rendered today by
 * `DatasetFacts.tsx`). One dry run on the dataset's own SQL, then one lens save carrying its
 * columns, dry-run stamp and referenced table: the same shape the version 1 to 2 backfill CLI
 * writes server-side (`src/jobs/le-lens-backfill-cli.ts` in the backend worktree), triggered from
 * the browser for whatever a bulk backfill has not reached, or a dataset the CLI itself refused
 * (A3's lane report: a card whose SQL fails its own dry run needs a human to fix the SQL first).
 *
 * `useDryRunReportingLeLensDataset` (`src/hooks/apis/reporting/useLeLensDatasetChecks.ts`) already
 * existed when this was written; a second, differently-named copy of the same three endpoints also
 * exists at `panel/actions/datasetApi.ts` (C2's). Reusing the `hooks/apis` one here rather than
 * adding a third, per the brief's "otherwise create declareApi.ts" instruction; flagged in the lane
 * report for D2 to dedupe.
 */

/** A dataset with a dry run's columns, stamp and referenced table applied. Exported so
 * UpgradeBanner.tsx can build the same shape for its bulk run without duplicating this mapping. */
export function declaredDataset(dataset: LensDataset, result: LensDatasetDryRunResponse): LensDataset {
  return {
    ...dataset,
    columns: result.columns,
    dryRun: {
      at: new Date().toISOString(),
      bytesProcessed: result.bytesProcessed,
      referencedTables: result.referencedTables,
    },
    // A dataset saved before `source` existed has none. Either way the table itself is now a fact
    // off the dry run rather than a guess, so it is always refreshed here.
    source: {
      kind: dataset.source?.kind ?? 'sql',
      ...(result.referencedTables.length === 1 ? { table: result.referencedTables[0] } : {}),
    },
  };
}

function replaceDataset(lens: Lens, datasetId: string, next: LensDataset) {
  return {
    ...lens.spec,
    datasets: (lens.spec.datasets ?? []).map((dataset) => (dataset.id === datasetId ? next : dataset)),
  };
}

export type UseDeclareNowResult = {
  /** Matches LensEditPanel's `onDeclareNow?: (datasetId: string) => void | Promise<void>` exactly
   *  (panel/panelTypes.ts / LensEditPanel.tsx, C1's): a mount point can pass this straight through. */
  onDeclareNow: (datasetId: string) => Promise<void>;
  /** Matches LensEditPanel's `declaring?: boolean`: true while any dataset's dry run is in flight. */
  declaring: boolean;
  /** The id of the dataset currently declaring, if any. `declaring` alone is enough for the panel
   *  (only one dataset panel is open at a time); exposed for a caller that shows more than one. */
  decliningDatasetId: string | null;
  /** The BigQuery or allowlist message from the last failed attempt, until cleared or retried. */
  error: string | null;
  clearError: () => void;
};

/** `lens` may be undefined while a lens is still loading; calling `onDeclareNow` before it
 *  resolves is a no-op rather than a crash, matching how the rest of the page guards a missing
 *  `lens` before rendering anything that can act on it. */
export function useDeclareNow(lens: Lens | undefined): UseDeclareNowResult {
  const dryRun = useDryRunReportingLeLensDataset();
  const updateLens = useUpdateReportingLeLens();
  const [decliningDatasetId, setDecliningDatasetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onDeclareNow = async (datasetId: string) => {
    if (!lens) return;
    const dataset = (lens.spec.datasets ?? []).find((candidate) => candidate.id === datasetId);
    if (!dataset) return;

    setError(null);
    setDecliningDatasetId(datasetId);
    try {
      const result = await dryRun.mutateAsync(dataset.sql);
      await updateLens.mutateAsync({
        id: lens.id,
        title: lens.title,
        description: lens.description,
        spec: replaceDataset(lens, datasetId, declaredDataset(dataset, result)),
        version: lens.version,
        successMessage: `${dataset.name}: columns declared`,
        // The dry run already read the dataset's own SQL; re-running every card's query for a
        // purely additive metadata change would bill BigQuery again for numbers that did not move.
        refetchData: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not declare this dataset’s columns.');
    } finally {
      setDecliningDatasetId(null);
    }
  };

  return {
    onDeclareNow,
    declaring: decliningDatasetId !== null,
    decliningDatasetId,
    error,
    clearError: () => setError(null),
  };
}
